import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database, UserCheck, Clock, Mail } from 'lucide-react';

const PrivacyPolicy = () => {
  const sections = [
    {
      icon: Eye,
      title: '1. Information We Collect',
      content: 'We collect information you provide directly to us, such as when you create an account, use our Salesforce AI Copilot services, or contact us for support.',
    },
    {
      icon: Database,
      title: '2. How We Use Your Information',
      content: 'We use the information we collect to provide, maintain, and improve our Salesforce AI Copilot services, process transactions, send communications, and comply with legal obligations.',
    },
    {
      icon: Lock,
      title: '3. Data Security',
      content: 'We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. Your data is encrypted both in transit and at rest.',
    },
    {
      icon: Shield,
      title: '4. GDPR Compliance',
      content: 'We are committed to compliance with the General Data Protection Regulation (GDPR). You have the right to access, rectify, or erase your personal data, and to restrict or object to its processing.',
    },
    {
      icon: UserCheck,
      title: '5. Data Sharing',
      content: 'We do not sell, rent, or share your personal information with third parties except as described in this policy or with your explicit consent. Your data is never shared with unauthorized parties.',
    },
    {
      icon: Clock,
      title: '6. Data Retention',
      content: 'We retain your personal data only for as long as necessary to provide our services and fulfill the purposes outlined in this policy, unless a longer retention period is required by law.',
    },
  ];

  return (
    <Layout>
      <SEOHead 
        title="Privacy Policy - NoQ5.ai"
        description="Learn how NoQ5.ai protects your data. Our privacy policy outlines how we collect, use, and safeguard your information."
        canonicalUrl="https://noq5.ai/privacy-policy"
      />
      <div className="container px-4 md:px-6 py-12 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="text-center space-y-4 mb-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4"
            >
              <Shield className="w-8 h-8 text-primary" />
            </motion.div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">Privacy Policy</h1>
            <p className="text-muted-foreground">Last updated: September 17, 2025</p>
          </div>

          {/* Sections */}
          <div className="space-y-6">
            {sections.map((section, index) => (
              <motion.section
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="bg-card border border-border rounded-2xl p-6 hover:border-primary/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <section.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold mb-3 text-foreground">{section.title}</h2>
                    <p className="text-muted-foreground leading-relaxed">{section.content}</p>
                  </div>
                </div>
              </motion.section>
            ))}

            {/* Contact Section */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-gradient-to-br from-primary/5 via-card to-primary/5 border border-primary/20 rounded-2xl p-6"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold mb-3 text-foreground">7. Contact Us</h2>
                  <p className="text-muted-foreground mb-4">
                    If you have any questions about this Privacy Policy, please contact us:
                  </p>
                  <div className="bg-card border border-border p-4 rounded-xl">
                    <p className="font-semibold text-foreground">NoQ5.ai Technologies Ltd.</p>
                    <p className="text-muted-foreground">
                      Email:{' '}
                      <a href="mailto:hello@noq5.ai" className="text-primary hover:underline">
                        hello@noq5.ai
                      </a>
                    </p>
                    <p className="text-muted-foreground">Address: Dublin, Ireland</p>
                  </div>
                </div>
              </div>
            </motion.section>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default PrivacyPolicy;